import React, { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, TextField, Typography } from '@mui/material'
import { Controller, FormProvider, useForm, useWatch, type SubmitHandler } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import GenericButton from '../../components/GenericButton'
import { ButtonType } from '../../components/GenericButton/styles'
import { notification } from '../../components/Notifications'
import { ReportPermissionManager } from '../../APIManager'
import { capitalizeFirst } from '../../utils/utils'
import { type ApiError } from '../../models/Common'
import { grey, red } from '../../colors'
import FormSelect, { type FormSelectOption } from '../ReportPermissionsFeature/FormSelect'
import { type BelowThresholdBehavior, type ResultsThreshold } from './types'

export interface ResultsThresholdFeatureProps {
  /** Distribution series id the threshold is configured for. */
  seriesId: string
  /** Series title shown in the modal subtitle. */
  scopeLabel?: string
  /** Series-level edit permission — hides Save and makes fields read-only when false. */
  canEdit: boolean
  isOpen: boolean
  handleCloseModal: () => void
}

interface ResultsThresholdFormData {
  /** Kept as a string so the field can be left empty (= feature off). */
  minimumResponseCount: string
  belowThresholdBehavior: BelowThresholdBehavior | ''
}

interface ResultsThresholdErrors {
  minimum?: string
  behavior?: string
}

export const resultsThresholdKey = (seriesId: string): unknown[] =>
  ['resultsThreshold', 'distributionSeries', seriesId]

const BEHAVIOR_OPTIONS: FormSelectOption[] = [
  { label: 'Hide results', value: 'hide' },
  { label: 'Show qualitative + instructor-added only', value: 'qualitative_and_instructor_added' }
]

const MIN_COUNT_ERROR = 'Minimum response count must be greater than 0.'
const BEHAVIOR_REQUIRED_ERROR =
  'A below-threshold behavior is required when a minimum response count is set.'
const LOAD_ERROR_MESSAGE = 'Failed to load results threshold. Please try again.'

const EMPTY_FORM: ResultsThresholdFormData = { minimumResponseCount: '', belowThresholdBehavior: '' }

/** Client-side mirror of the backend's two documented 422s. */
const validateForm = (form: ResultsThresholdFormData): ResultsThresholdErrors => {
  const errors: ResultsThresholdErrors = {}
  const raw = form.minimumResponseCount.trim()
  if (raw === '') return errors // no minimum set → feature off, nothing to validate
  const count = Number(raw)
  if (!Number.isInteger(count) || count < 1) {
    errors.minimum = MIN_COUNT_ERROR
  }
  if (form.belowThresholdBehavior === '') {
    errors.behavior = BEHAVIOR_REQUIRED_ERROR
  }
  return errors
}

const hasErrors = (errors: ResultsThresholdErrors): boolean =>
  errors.minimum != null || errors.behavior != null

/** Full-state PATCH body: empty minimum clears the floor (both fields null). */
const buildPayload = (form: ResultsThresholdFormData): ResultsThreshold => {
  const raw = form.minimumResponseCount.trim()
  if (raw === '') return { minimumResponseCount: null, belowThresholdBehavior: null }
  return {
    minimumResponseCount: Number(raw),
    belowThresholdBehavior: form.belowThresholdBehavior === '' ? null : form.belowThresholdBehavior
  }
}

const toFormValues = (data: ResultsThreshold | undefined): ResultsThresholdFormData => ({
  minimumResponseCount: data?.minimumResponseCount != null ? String(data.minimumResponseCount) : '',
  belowThresholdBehavior: data?.belowThresholdBehavior ?? ''
})

const ResultsThresholdFeature: React.FC<ResultsThresholdFeatureProps> = ({
  seriesId,
  scopeLabel,
  canEdit,
  isOpen,
  handleCloseModal
}) => {
  const queryClient = useQueryClient()

  const methods = useForm<ResultsThresholdFormData>({ defaultValues: EMPTY_FORM })
  const { control, handleSubmit, reset, formState: { isDirty } } = methods

  const watched = useWatch({ control }) as ResultsThresholdFormData
  const errors = validateForm(watched ?? EMPTY_FORM)

  // Validation messages surface only after a Save attempt, and clear on the next edit.
  const [submitAttempted, setSubmitAttempted] = useState(false)
  useEffect(() => {
    const subscription = methods.watch(() => { setSubmitAttempted(false) })
    return () => { subscription.unsubscribe() }
  }, [methods])

  const thresholdQuery = useQuery({
    queryKey: resultsThresholdKey(seriesId),
    queryFn: async (): Promise<ResultsThreshold> =>
      (await ReportPermissionManager.getSeriesResultsThreshold(seriesId)).data,
    enabled: isOpen && seriesId !== '',
    retry: false
  })

  const isLoading = thresholdQuery.isPending && thresholdQuery.fetchStatus !== 'idle'
  const loadFailed = thresholdQuery.isError
  // Never allow Save after a failed load — empty form + Save would PATCH nulls and wipe a
  // configured floor. Intentional disable still works after a successful GET with nulls.
  const canSave = canEdit && !loadFailed && thresholdQuery.isSuccess

  // Seed from a successful GET. Reseed while the form is still pristine so a mount refetch
  // (fresher than a stale cache entry) can replace the first paint without wiping edits.
  const seededRef = useRef(false)
  useEffect(() => {
    if (!isOpen) { seededRef.current = false; return }
    if (!thresholdQuery.isSuccess) return
    if (seededRef.current && isDirty) return
    seededRef.current = true
    reset(toFormValues(thresholdQuery.data))
  }, [isOpen, thresholdQuery.isSuccess, thresholdQuery.data, isDirty, reset])

  const saveMutation = useMutation({
    // Local onSubmit catch owns error toasts; silence the global mutation subscriber.
    meta: { silent: true },
    // async is required by @typescript-eslint/promise-function-async.
    // Return the Promise directly (no `return await`): SonarLint FPs that pattern as
    // "awaited value that is not a promise" on AxiosResponses. Disable return-await for
    // this line only — same idea as file-level disables elsewhere in the repo.
    mutationFn: async (form: ResultsThresholdFormData) => {
      // eslint-disable-next-line @typescript-eslint/return-await -- see comment above
      return ReportPermissionManager.saveSeriesResultsThreshold(seriesId, buildPayload(form))
    },
    onSuccess: (response) => {
      // The PATCH returns the stored threshold (same shape as GET), so write it straight into
      // the cache instead of triggering a redundant refetch.
      queryClient.setQueryData(resultsThresholdKey(seriesId), response.data)
    }
  })

  const onSubmit: SubmitHandler<ResultsThresholdFormData> = async (data) => {
    if (!canSave) return
    if (hasErrors(validateForm(data))) {
      setSubmitAttempted(true)
      notification('Please resolve the highlighted issues before saving.', { variant: 'error' })
      return
    }
    try {
      await saveMutation.mutateAsync(data)
      notification('Results threshold saved successfully.', { variant: 'success' })
      handleCloseModal()
    } catch (error) {
      // The api interceptor rejects with a structured { status, message } object.
      const apiError = error as Partial<ApiError>
      if (apiError.status === 404) {
        notification('This survey project no longer exists. Please refresh and try again.', { variant: 'error' })
        handleCloseModal()
        return
      }
      notification(apiError.message ?? 'Failed to save results threshold.', { variant: 'error' })
    }
  }

  const subtitle = scopeLabel != null && scopeLabel !== ''
    ? `Results Threshold for "${capitalizeFirst(scopeLabel)}"`
    : 'Results Threshold'

  return (
    <GenericModal
      isOpen={isOpen}
      maxWidth="sm"
      onClose={handleCloseModal}
      title={
        <Box>
          <Typography fontWeight={700} fontSize="18px">Results Threshold</Typography>
          <Typography fontSize="14px" color={grey[850]} sx={{ mt: 1, fontWeight: 400 }}>
            To protect respondent anonymity, results are suppressed for any section with fewer
            responses than the minimum you set here. Leave the minimum empty to turn this off.
          </Typography>
        </Box>
      }
      action={
        <DefaultModalActions
          destructiveButtonProps={{ onClick: handleCloseModal, text: 'Cancel', type: 'button' }}
          confirmButtonProps={{
            text: 'Save',
            type: 'button',
            hideButton: !canSave,
            loading: saveMutation.isPending,
            onClick: () => { void handleSubmit(onSubmit)() }
          }}
        />
      }
    >
      {isLoading
        ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )
        : loadFailed
          ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
              <Typography sx={{ fontSize: '14px', color: red[700], textAlign: 'center' }}>
                {LOAD_ERROR_MESSAGE}
              </Typography>
              <GenericButton
                label="Retry"
                buttonType={ButtonType.Secondary}
                onClick={() => { void thresholdQuery.refetch() }}
              />
            </Box>
            )
          : (
            <FormProvider {...methods}>
              <form>
                <Typography sx={{ fontSize: '16px', color: grey[250], fontWeight: 600, mb: 3 }}>
                  {subtitle}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="label"
                    htmlFor="results-threshold-min-count"
                    sx={{ display: 'block', fontSize: '14px', color: grey[250], fontWeight: 600, mb: 1 }}
                  >
                    Minimum response count
                  </Typography>
                  <Controller
                    name="minimumResponseCount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="results-threshold-min-count"
                        type="number"
                        fullWidth
                        size="small"
                        disabled={!canEdit}
                        placeholder="No minimum set"
                        inputProps={{ min: 1, step: 1, 'aria-label': 'Minimum response count' }}
                        error={submitAttempted && errors.minimum != null}
                        sx={{ backgroundColor: grey[0] }}
                      />
                    )}
                  />
                  {submitAttempted && errors.minimum != null && (
                    <Typography sx={{ fontSize: '13px', color: red[700], mt: 0.5 }}>{errors.minimum}</Typography>
                  )}
                </Box>

                <Box>
                  <FormSelect
                    name="belowThresholdBehavior"
                    label="Below-threshold behavior"
                    options={BEHAVIOR_OPTIONS}
                    disabled={!canEdit}
                  />
                  {submitAttempted && errors.behavior != null && (
                    <Typography sx={{ fontSize: '13px', color: red[700], mt: 0.5 }}>{errors.behavior}</Typography>
                  )}
                </Box>
              </form>
            </FormProvider>
            )}
    </GenericModal>
  )
}

export default ResultsThresholdFeature
